import { getAuth, signInWithEmailLink, isSignInWithEmailLink } from 'firebase/auth';
import notificationService from './notificationService';
import { isInternalPortalMode } from '@utils/portalHelper';

// Initialize Firebase Auth
const auth = getAuth();

/**
 * Process email link sign-in
 */
async function processSignIn(oobCode: string, continueUrl: string): Promise<void> {
  try {
    // Get email from localStorage or prompt
    let email = localStorage.getItem('emailForSignIn');
    if (!email) {
      email = prompt('Please provide your email for confirmation:');
      if (!email) {
        console.error('Email is required for sign-in');
        return;
      }
    }

    // Verify and sign in
    const signInLink = window.location.href;
    if (!isSignInWithEmailLink(auth, signInLink)) {
      console.error('Invalid sign-in link');
      notificationService.error('Invalid sign-in link. Please request a new one.');
      return;
    }

    const result = await signInWithEmailLink(auth, email, signInLink);

    // Check portal mode and validate permissions
    const isInternal = isInternalPortalMode();

    if (!isInternal) {
      // Regular app: validate tenantAdmins claim
      const tokenResult = await result.user.getIdTokenResult(true);
      const customClaims = tokenResult.claims;
      const tenantAdmins = customClaims.tenantAdmins as string[] | undefined;

      if (!tenantAdmins || tenantAdmins.length === 0) {
        console.warn('User is not an admin of any tenant');
        await auth.signOut();
        localStorage.removeItem('emailForSignIn');
        window.history.replaceState({}, document.title, window.location.pathname);
        return;
      }
    }
    // Internal portal: skip tenantAdmins validation (uses InternalAdminContext for auth)

    // Success - notify and redirect
    notificationService.success(`Successfully signed in as ${result.user.email}`);
    localStorage.removeItem('emailForSignIn');

    // Update URL, preserving internal flag if needed
    const basePath = window.location.pathname;
    window.history.replaceState({}, document.title, isInternal ? `${basePath}?internal=true` : basePath);

    // Broadcast to other tabs
    if (window.BroadcastChannel) {
      const channel = new BroadcastChannel('latte_auth_channel');
      channel.postMessage({
        type: 'signin_success',
        user: { uid: result.user.uid, email: result.user.email, displayName: result.user.displayName },
        timestamp: Date.now()
      });
      channel.close();
    }

    // Redirect after short delay
    setTimeout(() => {
      if (continueUrl) {
        window.location.href = continueUrl;
      } else if (isInternal) {
        window.location.href = '/?internal=true';
      } else {
        window.location.href = '/admin/intros/dashboard';
      }
    }, 1500);

  } catch (error: any) {
    console.error('Error signing in with email link:', error);

    // Handle specific error codes
    const errorMessages: Record<string, string> = {
      'auth/invalid-action-code': 'The sign-in link is invalid or has expired. Please request a new one.',
      'auth/invalid-email': 'Invalid email address. Please try again.',
      'auth/user-disabled': 'This account has been disabled. Please contact support.',
      'auth/expired-action-code': 'The sign-in link has expired. Please request a new one.',
    };

    const message = errorMessages[error.code] || error.message || 'Error signing in. Please try again.';
    notificationService.error(message);
  }
}

/**
 * Handle incoming auth messages from other windows
 */
async function handleAuthMessage(event: MessageEvent): Promise<void> {
  const data = event.data;
  if (data.type !== 'latte_auth') return;

  const { oobCode, mode, continueUrl } = data;
  if (mode === 'signIn' && oobCode) {
    await processSignIn(oobCode, continueUrl);
  }
}

/**
 * Check URL parameters for direct redirect (email link clicked)
 */
function checkUrlForAuthParams(): void {
  const urlParams = new URLSearchParams(window.location.search);

  const oobCode = urlParams.get('oobCode');
  const mode = urlParams.get('mode');
  const continueUrl = urlParams.get('continueUrl') || '';

  if (mode === 'signIn' && oobCode) {
    processSignIn(oobCode, continueUrl);
  }
}

/**
 * Initialize auth message listener
 */
export function initAuthMessageListener(): void {
  window.addEventListener('message', handleAuthMessage);
  checkUrlForAuthParams();
}

/**
 * Store email when sending sign-in link
 */
export function saveEmailForSignIn(email: string): void {
  localStorage.setItem('emailForSignIn', email);
}

// Initialize listener on import
if (typeof window !== 'undefined') {
  initAuthMessageListener();
}
